import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Send, Users, User, Image as ImageIcon, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ROLE_STYLE = {
  customer: "bg-slate-900 text-white",
  cleaner: "bg-teal-600 text-white",
  admin: "bg-amber-500 text-white",
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TeamChat({ jobId, currentUserId, compact = false }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // {path, previewUrl}
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/jobs/${jobId}/chat`);
      setData(r.data);
    } catch (e) {}
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [jobId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data?.messages?.length]);

  const pickImage = () => fileInputRef.current?.click();

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Only image files allowed"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPendingImage({ path: up.path, previewUrl: URL.createObjectURL(file) });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t && !pendingImage) return;
    setPosting(true);
    try {
      const payload = { text: t };
      if (pendingImage) payload.image_path = pendingImage.path;
      await api.post(`/jobs/${jobId}/chat`, payload);
      setText("");
      if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
      setPendingImage(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setPosting(false);
    }
  };

  const imgSrc = (path) => `${BACKEND}/api/files/${path}?auth=${localStorage.getItem("bb_token")}`;

  if (!data) return null;

  return (
    <Card className="border-slate-200" data-testid={`team-chat-${jobId}`}>
      <CardContent className={compact ? "p-4" : "p-5"}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-teal-600" />
            <div className="font-display font-semibold text-slate-900">Team chat</div>
            {data.team_size > 1 && (
              <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-[10px] font-mono uppercase">
                <Users className="w-3 h-3 mr-1" />{data.participants.length} participants
              </Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {data.participants.map((p) => (
            <span key={p.id} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase ${ROLE_STYLE[p.role] || "bg-slate-200"}`}>
              <User className="w-3 h-3" />{p.full_name?.split(" ")[0]}
            </span>
          ))}
        </div>
        <div ref={scrollRef} className={`space-y-2 overflow-y-auto ${compact ? "max-h-56" : "max-h-80"} bg-slate-50 rounded-xl p-3 border border-slate-100`}>
          {data.messages.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-6">No messages yet. Say hi 👋</div>
          )}
          {data.messages.map((m) => {
            const mine = m.user_id === currentUserId;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 ${mine ? "bg-teal-600 text-white" : "bg-white border border-slate-200 text-slate-900"}`}>
                  {!mine && (
                    <div className={`text-[10px] font-mono uppercase tracking-wider mb-0.5 ${mine ? "text-emerald-100" : "text-slate-500"}`}>
                      {m.sender_name} · {m.sender_role}
                    </div>
                  )}
                  {m.image_path && (
                    <a href={imgSrc(m.image_path)} target="_blank" rel="noreferrer">
                      <img
                        src={imgSrc(m.image_path)}
                        alt="chat attachment"
                        data-testid={`chat-img-${m.id}`}
                        className="rounded-lg mb-1 max-h-56 w-auto border border-white/20"
                        loading="lazy"
                      />
                    </a>
                  )}
                  {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                  <div className={`text-[10px] mt-1 font-mono ${mine ? "text-emerald-100" : "text-slate-400"}`}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {pendingImage && (
          <div className="mt-3 flex items-center gap-3 p-2 border border-slate-200 rounded-lg bg-white" data-testid={`chat-pending-${jobId}`}>
            <img src={pendingImage.previewUrl} alt="preview" className="h-12 w-12 object-cover rounded" />
            <div className="text-xs text-slate-500 flex-1 truncate">Ready to send · condition check photo</div>
            <Button size="sm" variant="ghost" onClick={() => setPendingImage(null)} data-testid="chat-cancel-upload">
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="mt-3 flex gap-2">
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid={`chat-file-input-${jobId}`} />
          <Button type="button" variant="outline" onClick={pickImage} disabled={uploading} data-testid={`chat-upload-${jobId}`} className="border-slate-300">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
          </Button>
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Coordinate arrival, access codes, ETA…"
            data-testid={`chat-input-${jobId}`}
          />
          <Button type="submit" disabled={posting || (!text.trim() && !pendingImage)} data-testid={`chat-send-${jobId}`} className="bg-slate-900 hover:bg-slate-800 text-white">
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
